import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { Access_Functions } from './shared/constants';

const routes: Routes = [
    { path: '', redirectTo: 'login', pathMatch: 'full' },
    {
        path: 'dashboard',
        loadChildren: () =>
            import('./layouts/private_layouts/dashboard/dashboard.module').then(
                (m) => m.DashboardModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.CUSTOMER_FULFILMENT_GRAPH_READ,
                    Access_Functions?.CUSTOMER_PARCEL_GRAPH_READ,
                    Access_Functions?.CUSTOMER_SHIPMENT_GRAPH_READ,
                    Access_Functions?.CUSTOMER_PRODUCT_GRAPH_READ,
                    Access_Functions?.CUSTOMER_INVENTORY_GRAPH_READ
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'shipments',
        loadChildren: () =>
            import('./layouts/private_layouts/shipments/shipments.module').then(
                (m) => m.ShipmentsModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: ['SUPPLIER_READ'],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'accounts',
        loadChildren: () =>
            import('./layouts/private_layouts/accounts/accounts.module').then(
                (m) => m.AccountsModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.MY_INFORMATION_READ,
                    Access_Functions?.MY_INFORMATION_WRITE,
                    Access_Functions?.CUSTOMER_USER_MANAGEMENT_READ,
                    Access_Functions?.CUSTOMER_USER_MANAGEMENT_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_PRINTER_SETUP_READ,
                    Access_Functions?.CUSTOMER_CONFIG_PRINTER_SETUP_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_SSO_READ,
                    Access_Functions?.CUSTOMER_CONFIG_SSO_WRITE,
                    Access_Functions?.DISPATCH_NOTE_READ,
                    Access_Functions?.DISPATCH_NOTE_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'products',
        loadChildren: () =>
            import('./layouts/private_layouts/products/products.module').then(
                (m) => m.ProductsModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.INVENTORY_PRODUCT_READ,
                    Access_Functions?.INVENTORY_PRODUCT_WRITE,
                    Access_Functions?.KIT_PRODUCT_READ,
                    Access_Functions?.KIT_PRODUCT_WRITE,
                    Access_Functions?.INVENTORY_BULK_ACTIVITY_READ,
                    Access_Functions?.INVENTORY_BULK_ACTIVITY_WRITE,
                    Access_Functions?.INVENTORY_CHANNEL_PRODUCT_READ,
                    Access_Functions?.INVENTORY_CHANNEL_PRODUCT_WRITE,
                    Access_Functions?.INVENTORY_UNSYNC_PRODUCT_READ,
                    Access_Functions?.INVENTORY_UNSYNC_PRODUCT_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'products-configuration',
        loadChildren: () =>
            import(
                './layouts/private_layouts/products-configuration/products-configuration.module'
            ).then((m) => m.ProductsConfigurationModule),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.INVENTORY_CHANNEL_PRODUCT_READ,
                    Access_Functions?.INVENTORY_CHANNEL_PRODUCT_WRITE,
                    Access_Functions?.INVENTORY_BULK_ACTIVITY_READ,
                    Access_Functions?.INVENTORY_BULK_ACTIVITY_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'supplier',
        loadChildren: () =>
            import('./layouts/private_layouts/suppliers/suppliers.module').then(
                (m) => m.SuppliersModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.MANAGE_SUPPLIER_READ,
                    Access_Functions?.MANAGE_SUPPLIER_WRITE,
                    Access_Functions?.SUPPLIER_BULK_ACTIVITY_READ,
                    Access_Functions?.SUPPLIER_BULK_ACTIVITY_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'report',
        loadChildren: () =>
            import(
                './layouts/private_layouts/manage-report/manage-report.module'
            ).then((m) => m.ManageReportModule),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.MANAGE_REPORT_READ,
                    Access_Functions?.MANAGE_REPORT_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'order',
        loadChildren: () =>
            import('./layouts/private_layouts/orders/orders.module').then(
                (m) => m.OrdersModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.DRAFT_ORDER_READ,
                    Access_Functions?.DRAFT_ORDER_WRITE,
                    Access_Functions?.CONFIRMED_ORDER_READ,
                    Access_Functions?.CONFIRMED_ORDER_WRITE,
                    Access_Functions?.RETURN_ORDER_READ,
                    Access_Functions?.RETURN_ORDER_WRITE,
                    Access_Functions?.MARKETPLACE_ORDER_READ,
                    Access_Functions?.MARKETPLACE_ORDER_WRITE,
                    Access_Functions?.ORDER_BULK_ACTIVITY_READ,
                    Access_Functions?.ORDER_BULK_ACTIVITY_WRITE,
                    Access_Functions?.ORDER_ACTION_REQUIRED_READ,
                    Access_Functions?.ORDER_ACTION_REQUIRED_WRITE,
                    Access_Functions?.SUCCESSFUL_ORDER_READ,
                    Access_Functions?.SUCCESSFUL_ORDER_WRITE,
                    Access_Functions?.ECOMMERCE_ORDER_READ,
                    Access_Functions?.ECOMMERCE_ORDER_WRITE,
                    Access_Functions?.ORDER_PARCEL_READ,
                    Access_Functions?.ORDER_PARCEL_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'purchase-orders',
        loadChildren: () =>
            import('./layouts/private_layouts/purchase-orders/po.module').then(
                (m) => m.PurchaseOrderModule
            ),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.INBOUND_CONSIGNMENT_READ,
                    Access_Functions?.INBOUND_CONSIGNMENT_WRITE,
                    Access_Functions?.INBOUND_GOODS_RECEIVED_READ,
                    Access_Functions?.INBOUND_GOODS_RECEIVED_WRITE,
                    Access_Functions?.INBOUND_BULK_ACTIVITY_READ,
                    Access_Functions?.INBOUND_BULK_ACTIVITY_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        path: 'integration',
        loadChildren: () =>
            import(
                './layouts/private_layouts/integration/integration.module'
            ).then((m) => m.IntegrationModule),
        canActivate: [AuthGuard, NgxPermissionsGuard],
        data: {
            permissions: {
                only: [
                    Access_Functions?.INTEGRATION_MARKETPLACE_READ,
                    Access_Functions?.INTEGRATION_MARKETPLACE_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_CARRIER_STORE_READ,
                    Access_Functions?.CUSTOMER_CONFIG_CARRIER_STORE_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_BULK_ACTIVITY_READ,
                    Access_Functions?.CUSTOMER_CONFIG_BULK_ACTIVITY_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_ECOMMERCE_READ,
                    Access_Functions?.CUSTOMER_CONFIG_ECOMMERCE_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_SERVICE_MAPPING_READ,
                    Access_Functions?.CUSTOMER_CONFIG_SERVICE_MAPPING_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_PRINTER_SETUP_READ,
                    Access_Functions?.CUSTOMER_CONFIG_PRINTER_SETUP_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_DEFAULT_PARAMS_READ,
                    Access_Functions?.CUSTOMER_CONFIG_DEFAULT_PARAMS_WRITE,
                    Access_Functions?.CUSTOMER_CONFIG_LABEL_FORMAT_READ,
                    Access_Functions?.CUSTOMER_CONFIG_LABEL_FORMAT_WRITE
                ],
                redirectTo: 'page-not-found'
            }
        }
    },
    {
        // Must stay LAST: `path: ''` prefix-matches every URL, so it only gets a
        // turn after each feature route above has failed to match. Holds every
        // unauthenticated page plus the `**` fallback — the router does not
        // backtrack out of a lazy module, so that wildcard has to live inside it.
        path: '',
        loadChildren: () =>
            import('./layouts/public_layouts/public.module').then(
                (m) => m.PublicModule
            )
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {})],
    exports: [RouterModule]
})
export class AppRoutingModule {}
